import { useEffect, useState } from "react";
import { useDataTable } from "../../../../../shared/hooks/DataTable";
import { InvoiceSummaryService } from "../../InvoiceSummaryService";
import {
  ODataPeriod,
  OInvoiceLookUpField,
  OInvoiceSummaryField,
} from "../../../../../shared/constants";
import type { InvoiceLookUpTableProps } from "./summaryDetailsTableConsts";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { FormattedCurrency } from "../../../../../shared/component/FormattedCurrency";
import { IamColumn } from "../../../../../shared/hooks/DataTableBasic";
import moment from "moment";
import { useTranslation } from "../../../../../shared/language";

export const InvoiceLookUpTableDialog = ({
  setLookupData,
  filterData,
  onHide,
  open,
  deletedItems,
}: InvoiceLookUpTableProps) => {
  const { t } = useTranslation();
  // Add state to store the full data from API
  const [fullData, setFullData] = useState([]);

  const InvoiceLLookUpColumns: IamColumn[] = [
    {
      field: OInvoiceLookUpField.INVOICE_SERIAL,
      header: t("common.symbol"),
      width: 120,
      sortable: false,
      matchMode: "contains",
    },
    {
      field: OInvoiceLookUpField.INVOICE_NUMBER,
      header: t("common.invoiceNo"),
      width: 120,
      sortable: false,
      matchMode: "contains",
    },
    {
      field: OInvoiceLookUpField.BUYER_TAX_CODE,
      header: t("common.taxCode"),
      width: 120,
      sortable: false,
      matchMode: "contains",
    },
    {
      field: OInvoiceLookUpField.BUYER_BUDGET_UNIT_CODE,
      header: t("common.budgetUnitCode"),
      sortable: false,
      width: 190,
      matchMode: "contains",
    },
    {
      field: OInvoiceLookUpField.BUYER_NAME,
      header: t("common.buyerName"),
      width: 250,
      sortable: false,
      matchMode: "contains",
    },
    {
      field: OInvoiceLookUpField.TOTAL_AMOUNT,
      header: t("report.totalAfterTax"),
      width: 150,
      sortable: false,
      dataType: "custom",
      matchMode: "contains",
      filterType: "number",
      customCell: (item) => (
        <FormattedCurrency
          value={item[OInvoiceLookUpField.TOTAL_AMOUNT]}
          currency={filterData.currencyCode}
        />
      ),
    },
    {
      field: OInvoiceLookUpField.DATE_SIGN,
      header: t("report.signDate"),
      dataType: "date-time",
      sortable: false,
      width: 135,
    },
    {
      field: OInvoiceLookUpField.CREATE_TIME,
      header: t("common.invoiceCreationDate"),
      width: 120,
      sortable: false,
      dataType: "date-time",
    },
    {
      field: OInvoiceLookUpField.CREATOR_NAME,
      header: t("report.createdBy"),
      width: 200,
      sortable: false,
      matchMode: "contains",
    },
    {
      field: OInvoiceLookUpField.EXCHANGE_RATE,
      header: t("report.exchangeRate"),
      width: 120,
      sortable: false,
      dataType: "custom",
      matchMode: "contains",
      customCell: (item) => (
        <FormattedCurrency
          value={item[OInvoiceLookUpField.EXCHANGE_RATE]}
          currency={filterData.currencyCode}
        />
      ),
    },
    {
      field: OInvoiceLookUpField.TOTAL_FEE,
      header: t("report.totalFee"),
      width: 150,
      sortable: false,
      dataType: "custom",
      matchMode: "contains",
      customCell: (item) => (
        <FormattedCurrency
          value={item[OInvoiceLookUpField.TOTAL_FEE]}
          currency={filterData.currencyCode}
        />
      ),
    },
    {
      field: OInvoiceLookUpField.TOTAL_DISCOUNT,
      header: t("report.totalOtherDeductions"),
      width: 150,
      sortable: false,
      dataType: "custom",
      matchMode: "contains",
      customCell: (item) => (
        <FormattedCurrency
          value={item[OInvoiceLookUpField.TOTAL_DISCOUNT]}
          currency={filterData.currencyCode}
        />
      ),
    },
  ];
  const {
    render: renderDataTable,
    refreshLazyData,
    selectedItems,
    setSelectedItems,
  } = useDataTable({
    indexColumnWidth: 50,
    columns: InvoiceLLookUpColumns,
    selectionMode: "checkbox",
    getList: async (params) => {
      if (
        !filterData.serial ||
        (filterData.dataPeriod !== ODataPeriod.DAY && !filterData.year)
      )
        return Promise.resolve({ items: [], totalCount: 0 });
      const getInvoiceLookupsParams: Record<string, any> = {
        dataPeriod: filterData.dataPeriod,

        currencyCode: filterData.currencyCode,
        invoiceSerial: filterData.serial,
      };
      if (filterData.dataPeriod !== ODataPeriod.DAY) {
        getInvoiceLookupsParams[OInvoiceSummaryField.MONTH_OR_QUARTER] =
          parseInt(filterData.monthOrQuarter);
        getInvoiceLookupsParams.year = parseInt(filterData.year);
      }
      if (filterData.dataPeriod === ODataPeriod.DAY) {
        getInvoiceLookupsParams[OInvoiceSummaryField.DATA_PERIOD_DATE] = moment(
          filterData.dataPeriodDate
        ).format("YYYY-MM-DD");
      }

      const skipCount = params.skipCount || 0;
      const maxResultCount = params.maxResultCount || 50; // Default to 50 items per page

      // If we already have the data, use it for pagination
      if (fullData.length > 0) {
        let filteredData = [...fullData];

        // Apply filters from params, excluding skipCount and maxResultCount
        for (const key in params) {
          const paramValue = String(params[key]).toLowerCase();
          if (
            key !== "skipCount" &&
            key !== "maxResultCount" &&
            key !== OInvoiceLookUpField.TOTAL_FEE &&
            params[key] !== undefined &&
            params[key] !== null
          ) {
            filteredData = filteredData.filter((item) =>
              String(item[key]).toLowerCase().includes(paramValue)
            );
          } else if (key === OInvoiceLookUpField.TOTAL_FEE) {
            filteredData = filteredData.filter((item) => {
              if (item[key] === null) {
                const regex = new RegExp(`^${"0"}+$`);
                return regex.test(paramValue);
              }
              return String(item[key]).toLocaleLowerCase().includes(paramValue);
            });
          }
        }

        const paginatedData = filteredData.slice(
          skipCount,
          skipCount + maxResultCount
        );
        return Promise.resolve({
          items: paginatedData,
          totalCount: filteredData.length,
        });
      }

      // Otherwise fetch from API
      return InvoiceSummaryService.getInvoiceLookups(
        getInvoiceLookupsParams
      ).then((data) => {
        // Store the full data for future pagination
        setFullData(data);

        // Apply pagination to the initial data
        const paginatedData = data.slice(skipCount, skipCount + maxResultCount);
        return {
          items: paginatedData,
          totalCount: data.length,
        };
      });
    },
  });

  useEffect(() => {
    if (filterData && open) {
      // Reset fullData when dialog opens with new filter data
      setFullData([]);
      refreshLazyData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filterData]);

  const footer = (
    <div className='flex justify-content-center'>
      <Button
        label={t("common.close")}
        onClick={() => {
          onHide(false);
        }}
        className='p-button-outlined p-button-secondary p-button-sm mr-2'
      />
      <Button
        label={t("common.save")}
        className='p-button-outlined p-button-sm mr-2'
        onClick={() => {
          const lookups = Array.isArray(selectedItems)
            ? selectedItems
            : [selectedItems];
          setLookupData(lookups);
          onHide(false);
        }}
      />
    </div>
  );

  useEffect(() => {
    if (Array.isArray(deletedItems)) {
      setSelectedItems(
        selectedItems.filter(
          (item) =>
            !deletedItems.find(
              (deletedItem) => deletedItem.invoiceId === item.invoiceId
            )
        )
      );
    } else {
      setSelectedItems(
        selectedItems.filter(
          (item) => !(item.invoiceId === deletedItems.invoiceId)
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deletedItems]);

  return (
    <Dialog
      style={{ maxWidth: "100%" }}
      visible={open}
      header={t("report.summaryLookup")}
      footer={footer}
      onHide={() => onHide(false)}
    >
      <div className='grid w-full'>
        <div className='col-12'>{renderDataTable()}</div>
        <label className='col-12'>
          <span className='required'>* </span>
          {t("report.summaryLimitNotice")}
        </label>
      </div>
    </Dialog>
  );
};
